import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Settings,
  Save,
  Loader2,
  Globe,
  Image as ImageIcon,
  Type,
  Upload,
  CheckCircle,
  ExternalLink,
  Share2,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SiteSettings() {
  const [settings, setSettings] = useState({
    title: '',
    description: '',
    keywords: '',
    favicon_url: '',
    og_image_url: '',
    og_type: 'website',
    twitter_card: 'summary_large_image',
    theme_color: '#3B82F6'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingOgImage, setUploadingOgImage] = useState(false);
  
  const faviconInputRef = useRef(null);
  const ogImageInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/settings/site`);
      setSettings(response.data.settings);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings/site`, settings);
      toast.success('Configurações guardadas com sucesso!');
      
      // Reload the page to apply new meta tags
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error('Erro ao guardar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFavicon(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/admin/settings/upload-favicon`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, favicon_url: response.data.url }));
      toast.success('Favicon carregado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleOgImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingOgImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/admin/settings/upload-og-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, og_image_url: response.data.url }));
      toast.success('Imagem de partilha carregada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar imagem');
    } finally {
      setUploadingOgImage(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="site-settings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Settings className="w-7 h-7 text-blue-400" />
            Configurações do Site
          </h1>
          <p className="text-slate-400 mt-1">
            Favicon, título, SEO e meta tags para partilha em redes sociais
          </p>
        </div>
        
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-500 hover:bg-blue-600"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar Alterações
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-[#252F38] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Type className="w-5 h-5 text-blue-400" />
              Informações Básicas
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Título do Site</Label>
                <Input
                  value={settings.title}
                  onChange={(e) => setSettings(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="SGFI Capital - Smart Global..."
                  className="bg-[#1D262D] border-white/10 text-white mt-1"
                  data-testid="site-title-input"
                />
                <p className="text-slate-500 text-xs mt-1">Aparece na aba do navegador</p>
              </div>
              
              <div>
                <Label className="text-slate-300">Descrição</Label>
                <Textarea
                  value={settings.description}
                  onChange={(e) => setSettings(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição do site para motores de busca..."
                  className="bg-[#1D262D] border-white/10 text-white mt-1 min-h-[80px]"
                  data-testid="site-description-input"
                />
                <p className="text-slate-500 text-xs mt-1">Aparece nos resultados do Google</p>
              </div>
              
              <div>
                <Label className="text-slate-300">Palavras-chave (SEO)</Label>
                <Input
                  value={settings.keywords}
                  onChange={(e) => setSettings(prev => ({ ...prev, keywords: e.target.value }))}
                  placeholder="investimento, fundos, capital, portugal..."
                  className="bg-[#1D262D] border-white/10 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Cor do Tema</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="color"
                    value={settings.theme_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, theme_color: e.target.value }))}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.theme_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, theme_color: e.target.value }))}
                    placeholder="#3B82F6"
                    className="bg-[#1D262D] border-white/10 text-white flex-1 font-mono"
                  />
                </div>
                <p className="text-slate-500 text-xs mt-1">Cor da barra de endereço em móveis</p>
              </div>
            </div>
          </div>

          {/* Favicon */}
          <div className="bg-[#252F38] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-blue-400" />
              Favicon
            </h2>
            
            <div className="space-y-4">
              {settings.favicon_url ? (
                <div className="flex items-center gap-4 p-4 bg-[#1D262D] rounded-lg">
                  <img 
                    src={getFullUrl(settings.favicon_url)} 
                    alt="Favicon" 
                    className="w-16 h-16 object-contain bg-white/10 rounded-lg p-2"
                  />
                  <div className="flex-1">
                    <p className="text-white font-medium">Favicon atual</p>
                    <p className="text-slate-400 text-sm truncate">{settings.favicon_url}</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => faviconInputRef.current?.click()}
                    className="border-white/10 text-slate-300"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Substituir
                  </Button>
                </div>
              ) : (
                <div 
                  onClick={() => faviconInputRef.current?.click()}
                  className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500/50 hover:bg-white/5 transition-all"
                >
                  {uploadingFavicon ? (
                    <Loader2 className="w-10 h-10 text-blue-400 mx-auto animate-spin" />
                  ) : (
                    <>
                      <Globe className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                      <p className="text-slate-400">Clique para carregar o favicon</p>
                      <p className="text-slate-500 text-sm mt-1">ICO, PNG ou SVG (máx. 1MB)</p>
                    </>
                  )}
                </div>
              )}
              
              <input
                ref={faviconInputRef}
                type="file"
                accept=".ico,.png,.svg"
                onChange={handleFaviconUpload}
                className="hidden"
              />
              
              <Input
                value={settings.favicon_url}
                onChange={(e) => setSettings(prev => ({ ...prev, favicon_url: e.target.value }))}
                placeholder="Ou cole uma URL do favicon..."
                className="bg-[#1D262D] border-white/10 text-white"
              />
            </div>
          </div>

          {/* OG Image */}
          <div className="bg-[#252F38] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Share2 className="w-5 h-5 text-blue-400" />
              Imagem de Partilha (Open Graph)
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              Esta imagem aparece quando o link é partilhado em redes sociais (Facebook, Twitter, LinkedIn, WhatsApp)
            </p>
            
            <div className="space-y-4">
              {settings.og_image_url ? (
                <div className="space-y-3">
                  <img 
                    src={getFullUrl(settings.og_image_url)} 
                    alt="OG Image" 
                    className="w-full aspect-video object-cover rounded-lg"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => ogImageInputRef.current?.click()}
                      className="flex-1 border-white/10 text-slate-300"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Substituir Imagem
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  onClick={() => ogImageInputRef.current?.click()}
                  className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500/50 hover:bg-white/5 transition-all"
                >
                  {uploadingOgImage ? (
                    <Loader2 className="w-10 h-10 text-blue-400 mx-auto animate-spin" />
                  ) : (
                    <>
                      <ImageIcon className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                      <p className="text-slate-400">Clique para carregar imagem</p>
                      <p className="text-slate-500 text-sm mt-1">JPG, PNG ou WebP (1200x630px recomendado)</p>
                    </>
                  )}
                </div>
              )}
              
              <input
                ref={ogImageInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleOgImageUpload}
                className="hidden"
              />
              
              <Input
                value={settings.og_image_url}
                onChange={(e) => setSettings(prev => ({ ...prev, og_image_url: e.target.value }))}
                placeholder="Ou cole uma URL da imagem..."
                className="bg-[#1D262D] border-white/10 text-white"
              />
            </div>
          </div>
        </div>

        {/* Right Column - Preview */}
        <div className="space-y-6">
          {/* Google Preview */}
          <div className="bg-[#252F38] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Search className="w-5 h-5 text-blue-400" />
              Pré-visualização Google
            </h2>
            
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                {settings.favicon_url && (
                  <img 
                    src={getFullUrl(settings.favicon_url)} 
                    alt="" 
                    className="w-4 h-4"
                  />
                )}
                <span className="text-sm text-gray-600">{window.location.origin}</span>
              </div>
              <h3 className="text-xl text-blue-700 hover:underline cursor-pointer mb-1">
                {settings.title || 'Título do Site'}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2">
                {settings.description || 'Descrição do site aparecerá aqui...'}
              </p>
            </div>
          </div>

          {/* Social Preview */}
          <div className="bg-[#252F38] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Share2 className="w-5 h-5 text-blue-400" />
              Pré-visualização Redes Sociais
            </h2>
            
            <div className="bg-[#1D262D] rounded-lg overflow-hidden border border-white/10">
              {settings.og_image_url ? (
                <img 
                  src={getFullUrl(settings.og_image_url)} 
                  alt="" 
                  className="w-full aspect-video object-cover"
                />
              ) : (
                <div className="w-full aspect-video bg-gradient-to-br from-blue-500/20 to-emerald-500/10 flex items-center justify-center">
                  <ImageIcon className="w-16 h-16 text-slate-600" />
                </div>
              )}
              <div className="p-4">
                <p className="text-slate-500 text-xs uppercase mb-1">{window.location.host}</p>
                <h3 className="text-white font-semibold mb-1 line-clamp-2">
                  {settings.title || 'Título do Site'}
                </h3>
                <p className="text-slate-400 text-sm line-clamp-2">
                  {settings.description || 'Descrição do site...'}
                </p>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <h3 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Dicas para Melhor SEO
            </h3>
            <ul className="text-slate-400 text-sm space-y-1">
              <li>• Título: 50-60 caracteres</li>
              <li>• Descrição: 150-160 caracteres</li>
              <li>• Imagem OG: 1200x630 pixels</li>
              <li>• Favicon: 32x32 ou 16x16 pixels</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
